// Updated Documents.js component
import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { getStorage, ref, getDownloadURL, listAll } from 'firebase/storage';
import { getFirestore, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import StorageDiagnostic from './StorageDiagnostic';

function Documents({ user }) {
  const [documents, setDocuments] = useState([]);
  const [boardsBuiltThisYear, setBoardsBuiltThisYear] = useState(0);
  const [boardsRemaining, setBoardsRemaining] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  
  useEffect(() => {
    if (!user) return;
    
    const fetchDocuments = async () => {
      try {
        const db = getFirestore();
        const storage = getStorage();
        
        // Check if the user has a valid document in Firestore
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Check if the user has agreed to the license
          if (!userData.licenseAgreed) {
            window.location.href = "/complete-profile";
            return;
          }
          
          setBoardsBuiltThisYear(userData.yearlyBoardCount || 0);
          setBoardsRemaining(3 - (userData.yearlyBoardCount || 0));
          
          // Check if we need to reset the yearly count
          const lastReset = userData.lastResetDate?.toDate() || new Date();
          const currentYear = new Date().getFullYear();
          const lastResetYear = lastReset.getFullYear();
          
          if (lastResetYear < currentYear) {
            // Reset counter for the new year
            await setDoc(userRef, {
              ...userData,
              yearlyBoardCount: 0,
              lastResetDate: Timestamp.now()
            });
            setBoardsBuiltThisYear(0);
            setBoardsRemaining(3);
          }
          
          // Force token refresh before accessing storage
          await user.getIdToken(true);
          
          // Try to access the documents directory
          try {
            const documentsRef = ref(storage, 'documents');
            
            const documentsList = await listAll(documentsRef);
            
            if (documentsList.items.length === 0) {
              setDocuments([]);
              setLoading(false);
              return;
            }
            
            const docsWithUrls = await Promise.all(
              documentsList.items.map(async (item) => {
                try {
                  const url = await getDownloadURL(item);
                  return {
                    name: item.name,
                    url: url
                  };
                } catch (urlError) {
                  return {
                    name: item.name,
                    url: null,
                    error: urlError.message
                  };
                }
              })
            );
            
            setDocuments(docsWithUrls);
          } catch (storageError) {
            console.error("Storage error:", storageError);
            setError(`Storage error: ${storageError.message}. Code: ${storageError.code}`);
            setShowDiagnostics(true);
          }
        } else {
          setError("User profile not found in database. Please log out and back in.");
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching documents:", error);
        setError(`An error occurred: ${error.message}`);
        setShowDiagnostics(true);
        setLoading(false);
      }
    };
    
    fetchDocuments();
  }, [user]);
  
  const handleBoardBuilt = async () => {
    // ... existing board built logic ...
  };
  
  const handleRefreshToken = async () => {
    try {
      await auth.currentUser.getIdToken(true);
      window.location.reload();
    } catch (error) {
      console.error("Error refreshing token:", error);
      setError("Failed to refresh authentication: " + error.message);
    }
  };
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p>Loading documents...</p>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold">DIY Documentation</h1>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p className="font-bold">Error:</p>
          <p>{error}</p>
          <div className="flex flex-wrap justify-center mt-3">
            <button
              onClick={handleRefreshToken}
              className="bg-red-700 text-white px-4 py-2 m-2 rounded hover:bg-red-800"
            >
              Refresh Authentication
            </button>
            
            <Link
              to="/force-logout"
              className="bg-gray-700 text-white px-4 py-2 m-2 rounded hover:bg-gray-800 inline-flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Reset Authentication
            </Link>
            
            <Link
              to="/repair-profile"
              className="bg-blue-700 text-white px-4 py-2 m-2 rounded hover:bg-blue-800 inline-flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Repair User Profile
            </Link>
            
            <button
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              className="bg-yellow-600 text-white px-4 py-2 m-2 rounded hover:bg-yellow-700"
            >
              {showDiagnostics ? "Hide Diagnostics" : "Show Diagnostics"}
            </button>
          </div>
        </div>
      )}
      
      {showDiagnostics && <StorageDiagnostic />}
      
      <div className="bg-blue-100 p-4 rounded-lg mb-8">
        <h2 className="text-xl font-semibold mb-2">Your License Status</h2>
        <p className="mb-2">
          Boards built this year: <strong>{boardsBuiltThisYear} / 3</strong>
        </p>
        <p className="mb-4">
          Remaining boards you can build: <strong>{boardsRemaining}</strong>
        </p>
        <button 
          onClick={handleBoardBuilt}
          disabled={boardsBuiltThisYear >= 3}
          className={`px-4 py-2 rounded-md ${
            boardsBuiltThisYear >= 3 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          I Built a Board
        </button>
        {boardsBuiltThisYear >= 3 && (
          <p className="mt-2 text-sm text-red-600">
            You've reached your annual limit of 3 boards under the non-commercial license
          </p>
        )}
      </div>
      
      <div className="bg-gray-100 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Available Documentation</h2>
        
        {error ? (
          <div>
            <p>We're currently experiencing technical difficulties loading the documents.</p>
            <p className="mt-2 text-sm text-gray-600">
              This is likely due to a Firebase Storage permission issue. Please contact support@waka.surf for assistance.
            </p>
          </div>
        ) : documents.length === 0 ? (
          <p>No documents available at this time. Please check back later.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc, index) => (
              <div key={index} className="border rounded-md p-4 bg-white">
                <h3 className="font-medium mb-2">{doc.name}</h3>
                {doc.url ? (
                  <a 
                    href={doc.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Download Document
                  </a>
                ) : (
                  <p className="text-red-600">Error loading document link</p>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-6">
          <h3 className="font-semibold mb-2">Important Reminders:</h3>
          <ul className="list-disc pl-5">
            <li>Please report any improvements or modifications you make to the technology as required by the license</li>
            <li>Remember to include appropriate attribution on your board as specified in the license</li>
            <li>Do not distribute these documents to non-registered users</li>
            <li>For support or questions, contact support@waka.surf</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Documents;